import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { AnalyticsEvent, BookingSummary, Message, ServiceCard } from '../types'

type MessageContextValue = {
  messages: Message[]
  sendMessage: (content: string) => void
  hasInteracted: boolean
  isLoading: boolean
  error: string | null
}

type MessageProviderProps = {
  apiBaseUrl: string
  clientId: string
  userId: string
  chatbotId: string
  publicId: string
  welcomeMessage?: string
  emitEvent?: (event: AnalyticsEvent) => void
  onAutoStartBooking?: (serviceId?: string, isoDate?: string) => void
  onAutoStartInquiry?: () => void
  bookingSummary?: BookingSummary | null
  children: ReactNode
  onRegisterClear?: (clear: () => void) => void
}

const MessageContext = createContext<MessageContextValue | undefined>(undefined)

const makeId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)

const createBotMessage = (content: string): Message => ({
  id: `bot-${makeId()}`,
  sender: 'bot',
  content,
  timestamp: new Date().toISOString(),
})

const createUserMessage = (content: string): Message => ({
  id: `user-${makeId()}`,
  sender: 'user',
  content,
  timestamp: new Date().toISOString(),
})

const normalizeBaseUrl = (url: string) => url.replace(/\/$/, '')

const MAX_RESPONSE_ATTEMPTS = 2

const validateBotResponse = (content: string, serviceCard?: ServiceCard) => {
  const trimmed = content.trim()

  if (!trimmed && !serviceCard) {
    return { valid: false, reason: 'Empty response with no service card' }
  }

  const invalidPatterns = [
    /software development or mentorship/i,
    /services we offer/i,
    /would you like to explore one of these services/i,
    /```/,
  ]

  if (invalidPatterns.some((pattern) => pattern.test(trimmed))) {
    return { valid: false, reason: 'Contains fallback or placeholder text' }
  }

  if (serviceCard) {
    const hasServices = Array.isArray(serviceCard.services) && serviceCard.services.length > 0
    if (!hasServices) {
      return { valid: false, reason: 'Service card missing services' }
    }
  }

  return { valid: true }
}

export const MessageProvider = ({
  children,
  apiBaseUrl,
  clientId,
  userId,
  chatbotId,
  publicId,
  welcomeMessage,
  emitEvent,
  onAutoStartBooking,
  onAutoStartInquiry,
  bookingSummary,
  onRegisterClear,
}: MessageProviderProps) => {
  const storageKey = useMemo(
    () => `byhandle-chat-${publicId}-${userId}`,
    [publicId, userId],
  )

  const [messages, setMessages] = useState<Message[]>([])
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const summaryIdRef = useRef<string | null>(null)
  const hasHydratedRef = useRef(false)

  useEffect(() => {
    if (hasHydratedRef.current) return
    if (typeof window === 'undefined') return

    const saved = localStorage.getItem(storageKey)
    if (saved) {
      try {
        const parsed: Message[] = JSON.parse(saved)
        if (Array.isArray(parsed) && parsed.length) {
          setMessages(parsed)
          setHasInteracted(parsed.some((msg) => msg.sender === 'user'))
          hasHydratedRef.current = true
          return
        }
      } catch {
        // ignore parse errors
      }
    }

    const initial = welcomeMessage ? [createBotMessage(welcomeMessage)] : []
    setMessages(initial)
    setHasInteracted(false)
    hasHydratedRef.current = true
  }, [storageKey, welcomeMessage])

  useEffect(() => {
    if (!hasHydratedRef.current) return
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages))
    } catch {
      // ignore storage errors
    }
  }, [messages, storageKey])

  useEffect(() => {
    if (!bookingSummary) {
      summaryIdRef.current = null
      return
    }

    if (summaryIdRef.current === bookingSummary.id) return

    const summaryMessage = {
      ...createBotMessage("Appointment confirmed! Here's a quick summary:"),
      summary: bookingSummary,
    }

    setMessages((prev) => [...prev, summaryMessage])
    summaryIdRef.current = bookingSummary.id
  }, [bookingSummary])

  useEffect(() => {
    if (onRegisterClear) {
      const clear = () => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(storageKey)
        }
        setMessages(welcomeMessage ? [createBotMessage(welcomeMessage)] : [])
        setHasInteracted(false)
        setError(null)
      }
      onRegisterClear(clear)
    }
  }, [onRegisterClear, storageKey, welcomeMessage])

  const chatEndpoint = useMemo(() => {
    if (!apiBaseUrl) return '/api/chat'
    const normalized = normalizeBaseUrl(apiBaseUrl)
    return `${normalized}/api/chat`
  }, [apiBaseUrl])

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim()
      if (!trimmed) return
      if (isLoading) return // Prevent multiple concurrent requests

      const userMessage = createUserMessage(trimmed)
      setMessages((prev) => [...prev, userMessage])
      setHasInteracted(true)
      setIsLoading(true)
      setError(null)
      emitEvent?.({ type: 'message_sent', clientId, content: trimmed })

      try {
        // Build conversation history for API (skip messages with empty content)
        const conversationHistory = messages
          .filter((msg) => msg.content && msg.content.trim())
          .map((msg) => ({
            role: msg.sender === 'user' ? ('user' as const) : ('assistant' as const),
            content: msg.content,
          }))

        // Add the new user message
        conversationHistory.push({
          role: 'user' as const,
          content: trimmed,
        })
        let finalResponse: {
          messageContent: string
          showBookingButton: boolean
          autoStartBooking: boolean
          showInquiryButton: boolean
          showServicesButton: boolean
          servicesManageUrl?: string
          shouldSendFollowup: boolean
          serviceCard?: ServiceCard
          serviceId?: string
          isoDate?: string
        } | null = null
        let lastValidationError: string | null = null

        for (let attempt = 0; attempt < MAX_RESPONSE_ATTEMPTS; attempt++) {
          // Call the chat API
          const response = await fetch(chatEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              messages: conversationHistory,
              userId,
              publicId,
            }),
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))

            if (response.status === 429) {
              throw new Error('Too many messages. Please wait a moment and try again.')
            }

            throw new Error(errorData.error || 'Failed to get response. Please try again.')
          }

          const data = await response.json()

          if (!data.message) {
            throw new Error('Invalid response from server')
          }

          // Check if response contains booking markers
          let messageContent = data.message.content || ''
          let showBookingButton = false
          let autoStartBooking = false
          let showInquiryButton = false
          let shouldSendFollowup = false
          let showServicesButton = false
          let servicesManageUrl = data.manageServicesUrl as string | undefined

          let serviceCard: ServiceCard | undefined

          // Extract service card block
          const cardRegex = /\[SERVICE_CARD\]([\s\S]*?)\[\/SERVICE_CARD\]/
          const cardMatch = messageContent.match(cardRegex)
          if (cardMatch) {
            try {
              serviceCard = JSON.parse(cardMatch[1])
            } catch {
              // Ignore malformed JSON
            }
            messageContent = messageContent.replace(cardRegex, '').trim()
          }

          // Validate that we have either content or a service card
          if (!messageContent && !serviceCard) {
            lastValidationError = 'Invalid response from server'
          }

          // Check for inquiry flow trigger (highest priority - for human handoff and security)
          if (messageContent.includes('[AUTO_START_INQUIRY]')) {
            // Remove the marker from the displayed content
            messageContent = messageContent.replace(/\[AUTO_START_INQUIRY\]/g, '').trim()
            showInquiryButton = true
          } else if (messageContent.includes('[AUTO_START_BOOKING]')) {
            // Check for auto-start booking
            autoStartBooking = true
            // Remove the marker from the displayed content
            messageContent = messageContent.replace(/\[AUTO_START_BOOKING\]/g, '').trim()
          } else if (messageContent.includes('[SHOW_BOOKING_BUTTON]')) {
            showBookingButton = true
            // Remove the marker from the displayed content
            messageContent = messageContent.replace(/\[SHOW_BOOKING_BUTTON\]/g, '').trim()
          }

          // Check for follow-up message trigger
          shouldSendFollowup = messageContent.includes('[SEND_FOLLOWUP_MESSAGE]')
          messageContent = messageContent.replace(/\[SEND_FOLLOWUP_MESSAGE\]/g, '').trim()

          if (messageContent.includes('[SHOW_SERVICES_BUTTON]')) {
            showServicesButton = true
            servicesManageUrl = servicesManageUrl || '/dashboard/services'
            messageContent = messageContent.replace(/\[SHOW_SERVICES_BUTTON\]/g, '').trim()
          }

          const validation = validateBotResponse(messageContent, serviceCard)
          if (validation.valid) {
            finalResponse = {
              messageContent,
              showBookingButton,
              autoStartBooking,
              showInquiryButton,
              showServicesButton,
              servicesManageUrl,
              shouldSendFollowup,
              serviceCard,
              serviceId: data.serviceId,
              isoDate: data.isoDate,
            }
            break
          }

          lastValidationError = validation.reason || 'Invalid response from server'
          console.warn('Bot response failed validation, retrying', {
            attempt: attempt + 1,
            reason: validation.reason,
          })
        }

        if (!finalResponse) {
          throw new Error(lastValidationError || 'Invalid response from server')
        }

        const {
          messageContent,
          showBookingButton,
          autoStartBooking,
          showInquiryButton,
          showServicesButton,
          servicesManageUrl,
          shouldSendFollowup,
          serviceCard,
          serviceId,
          isoDate,
        } = finalResponse

        const userRequestedMessage = /send a message|leave a message|send.*message|message the team|email the team/i.test(trimmed)
        const enhancedShowInquiry = showInquiryButton || userRequestedMessage
        const shouldAutoStartInquiry = userRequestedMessage && !showInquiryButton

        // Add bot response to messages
        const botMessage = {
          ...createBotMessage(messageContent),
          showBookingButton,
          autoStartBooking,
          showInquiryButton: enhancedShowInquiry,
          showServicesButton,
          servicesManageUrl,
          serviceCard,
        }
        setMessages((prev) => [...prev, botMessage])

        if (autoStartBooking) {
          setTimeout(() => {
            onAutoStartBooking?.(serviceId, isoDate)
          }, 2200)
        }

        if (shouldAutoStartInquiry && !autoStartBooking) {
          setTimeout(() => {
            onAutoStartInquiry?.()
          }, 800)
        }

        // Send follow-up message after a delay if marker was present
        if (shouldSendFollowup) {
          setTimeout(() => {
            const followupMessage = createBotMessage(
              "Is there a specific service you're interested in, or do you have any questions about what we offer?"
            )
            setMessages((prev) => [...prev, followupMessage])
          }, 1500)
        }
      } catch (err) {
        console.error('Chat API error:', err)
        const errorMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
        setError(errorMessage)

        // Add error message as bot response
        const errorBotMessage = {
          ...createBotMessage(`I'm sorry, I encountered an error: ${errorMessage}`),
          showInquiryButton: true,
        }
        setMessages((prev) => [...prev, errorBotMessage])
      } finally {
        setIsLoading(false)
      }
    },
    [
      chatEndpoint,
      clientId,
      userId,
      chatbotId,
      messages,
      welcomeMessage,
      isLoading,
      emitEvent,
      onAutoStartBooking,
      onAutoStartInquiry,
    ],
  )

  const value = useMemo(
    () => ({
      messages,
      sendMessage,
      hasInteracted,
      isLoading,
      error,
    }),
    [hasInteracted, messages, sendMessage, isLoading, error],
  )

  return <MessageContext.Provider value={value}>{children}</MessageContext.Provider>
}

export const useMessages = () => {
  const context = useContext(MessageContext)
  if (!context) {
    throw new Error('useMessages must be used inside a MessageProvider')
  }

  return context
}
